/*
🚨 Safety Intelligence Dashboard
GetMyHive Safety Intelligence operasyon arayüzü
*/

import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Alert,
  Table,
  Button,
  Input,
  Select,
  Modal,
  Form,
  message,
  Badge,
  Progress,
  Tabs,
  Space,
  Typography,
  Tag,
  Tooltip,
  InputNumber,
  Switch,
  Timeline,
  List,
  Avatar,
  Empty,
  Spin
} from 'antd';
import {
  SafetyOutlined,
  WarningOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  RadarChartOutlined,
  AlertOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ThunderboltOutlined,
  EyeOutlined,
  ReloadOutlined,
  SettingOutlined,
  BugOutlined,
  SafetyCertificateOutlined,
  RobotOutlined,
  SearchOutlined,
  FilterOutlined,
  LineChartOutlined,
  HeatMapOutlined,
  DatabaseOutlined,
  ApiOutlined,
  MonitorOutlined,
  DashboardOutlined,
  SecurityScanOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import { SafetyIntelligenceSettings, SafetyIntelligenceReports, ThreatDetailModal } from '../components/safety';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;
const { TextArea } = Input;

// Mock data - gerçek API'den gelecek
const mockThreats = [
  {
    id: '1',
    type: 'harassment_call',
    location: 'Kadıköy, İstanbul',
    severity: 'high',
    confidence: 0.87,
    timestamp: '2024-01-16 14:23',
    description: 'Şüpheli taciz arama tespit edildi'
  },
  {
    id: '2', 
    type: 'missing_person',
    location: 'Beyoğlu, İstanbul',
    severity: 'critical',
    confidence: 0.94,
    timestamp: '2024-01-16 13:45',
    description: 'Kayıp kişi raporu - acil durum'
  },
  {
    id: '3',
    type: 'crime_pattern',
    location: 'Şişli, İstanbul',
    severity: 'medium',
    confidence: 0.73,
    timestamp: '2024-01-16 12:15',
    description: 'Suç pattern analizi - artan risk'
  }
];

const mockDataSources = [
  { name: 'EGM Crime Database', status: 'active', lastUpdate: '2 dakika önce', health: 98 },
  { name: 'BTK Telecom Intelligence', status: 'active', lastUpdate: '5 dakika önce', health: 95 },
  { name: 'Istanbul Municipality', status: 'warning', lastUpdate: '15 dakika önce', health: 78 },
  { name: 'Missing Persons Registry', status: 'active', lastUpdate: '1 dakika önce', health: 99 }
];

const mockAlerts = [
  {
    id: '1',
    type: 'emergency',
    title: 'Yüksek Risk Bölgesi Tespit Edildi',
    message: 'Taksim bölgesinde artan güvenlik riski tespit edildi',
    timestamp: '2024-01-16 14:30',
    isActive: true
  },
  {
    id: '2', 
    type: 'warning',
    title: 'Telefon Taciz Artışı',
    message: 'Kadıköy bölgesinde telefon tacizi vakaları artış gösterdi',
    timestamp: '2024-01-16 13:20',
    isActive: true
  }
];

const SafetyIntelligenceDashboard: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [threatAnalysisModal, setThreatAnalysisModal] = useState(false);
  const [phoneAnalysisModal, setPhoneAnalysisModal] = useState(false);
  const [routeAnalysisModal, setRouteAnalysisModal] = useState(false);
  const [selectedThreat, setSelectedThreat] = useState<any>(null);
  const [threatDetailVisible, setThreatDetailVisible] = useState(false);
  const [form] = Form.useForm();

  // Stats
  const [stats, setStats] = useState({
    totalThreats: 247,
    activeAlerts: 12,
    processedRecords: 15847,
    systemHealth: 96
  });

  useEffect(() => {
    // Başlangıç verileri yükle
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Gerçek API çağrıları burada yapılacak
      await new Promise(resolve => setTimeout(resolve, 1000)); // Mock delay
      message.success('Dashboard verileri yüklendi');
    } catch (error) {
      message.error('Veri yükleme hatası');
    } finally {
      setLoading(false);
    }
  };

  const handleThreatAnalysis = async (values: any) => {
    setLoading(true);
    try {
      console.log('Threat Analysis:', values);
      // API çağrısı yapılacak
      await new Promise(resolve => setTimeout(resolve, 2000));
      message.success('Tehdit analizi tamamlandı');
      setThreatAnalysisModal(false);
      form.resetFields();
    } catch (error) {
      message.error('Analiz hatası');
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneAnalysis = async (values: any) => {
    setLoading(true);
    try {
      console.log('Phone Analysis:', values);
      await new Promise(resolve => setTimeout(resolve, 1500));
      message.success('Telefon analizi tamamlandı');
      setPhoneAnalysisModal(false);
      form.resetFields();
    } catch (error) {
      message.error('Telefon analizi hatası');
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return '#ff4d4f';
      case 'high': return '#ff7a45';
      case 'medium': return '#ffa940';
      case 'low': return '#52c41a';
      default: return '#d9d9d9';
    }
  };

  const getSeverityText = (severity: string) => {
    switch (severity) {
      case 'critical': return 'KRİTİK';
      case 'high': return 'YÜKSEK';
      case 'medium': return 'ORTA';
      case 'low': return 'DÜŞÜK';
      default: return 'BİLİNMEYEN';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge status="success" text="Aktif" />;
      case 'warning':
        return <Badge status="warning" text="Uyarı" />;
      case 'error':
        return <Badge status="error" text="Hata" />;
      default:
        return <Badge status="default" text="Bilinmiyor" />;
    }
  };

  // Main Statistics Cards
  const renderStatistics = () => (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col xs={24} sm={12} lg={6}>
        <Card>
          <Statistic
            title="Toplam Tehdit"
            value={stats.totalThreats}
            prefix={<SafetyCertificateOutlined style={{ color: '#ff4d4f' }} />}
            valueStyle={{ color: '#ff4d4f' }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Card>
          <Statistic
            title="Aktif Uyarılar"
            value={stats.activeAlerts}
            prefix={<AlertOutlined style={{ color: '#fa8c16' }} />}
            valueStyle={{ color: '#fa8c16' }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Card>
          <Statistic
            title="İşlenen Kayıt"
            value={stats.processedRecords}
            prefix={<DatabaseOutlined style={{ color: '#1890ff' }} />}
            valueStyle={{ color: '#1890ff' }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Card>
          <Statistic
            title="Sistem Sağlığı"
            value={stats.systemHealth}
            suffix="%"
            prefix={<MonitorOutlined style={{ color: '#52c41a' }} />}
            valueStyle={{ color: '#52c41a' }}
          />
        </Card>
      </Col>
    </Row>
  );

  // Overview Tab
  const renderOverview = () => (
    <Row gutter={[16, 16]}>
      <Col xs={24} lg={16}>
        <Card 
          title={
            <Space>
              <WarningOutlined />
              <span>Son Tehditler</span>
            </Space>
          }
          extra={
            <Button 
              icon={<ReloadOutlined />} 
              onClick={loadDashboardData}
              loading={loading}
            >
              Yenile
            </Button>
          }
        >
          <List
            dataSource={mockThreats}
            renderItem={(threat) => (
              <List.Item
                actions={[
                  <Button 
                    size="small" 
                    icon={<EyeOutlined />}
                    onClick={() => {
                      setSelectedThreat(threat);
                      setThreatDetailVisible(true);
                    }}
                  >
                    Detay
                  </Button>
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <Avatar 
                      icon={<ExclamationCircleOutlined />}
                      style={{ backgroundColor: getSeverityColor(threat.severity) }}
                    />
                  }
                  title={
                    <Space>
                      <span>{threat.description}</span>
                      <Tag color={getSeverityColor(threat.severity)}>
                        {getSeverityText(threat.severity)}
                      </Tag>
                    </Space>
                  }
                  description={
                    <Space direction="vertical" size="small">
                      <Text type="secondary">
                        <EnvironmentOutlined /> {threat.location}
                      </Text>
                      <Text type="secondary">
                        <ClockCircleOutlined /> {threat.timestamp}
                      </Text>
                      <Text type="secondary">
                        Güvenilirlik: {(threat.confidence * 100).toFixed(0)}%
                      </Text>
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        </Card>
      </Col>
      
      <Col xs={24} lg={8}>
        <Card 
          title={
            <Space>
              <DatabaseOutlined />
              <span>Veri Kaynakları</span>
            </Space>
          }
          style={{ marginBottom: 16 }}
        >
          <List
            dataSource={mockDataSources}
            renderItem={(source) => (
              <List.Item>
                <List.Item.Meta
                  title={
                    <Space>
                      <span>{source.name}</span>
                      {getStatusBadge(source.status)}
                    </Space>
                  }
                  description={
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <Text type="secondary">{source.lastUpdate}</Text>
                      <Progress 
                        percent={source.health} 
                        size="small"
                        strokeColor={source.health > 90 ? '#52c41a' : source.health > 70 ? '#fa8c16' : '#ff4d4f'}
                      />
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        </Card>

        <Card 
          title={
            <Space>
              <AlertOutlined />
              <span>Aktif Uyarılar</span>
            </Space>
          }
        >
          <Timeline>
            {mockAlerts.map((alert) => (
              <Timeline.Item
                key={alert.id}
                color={alert.type === 'emergency' ? 'red' : 'orange'}
                dot={
                  alert.type === 'emergency' 
                    ? <ExclamationCircleOutlined style={{ fontSize: '16px' }} />
                    : <WarningOutlined style={{ fontSize: '16px' }} />
                }
              >
                <div>
                  <Text strong>{alert.title}</Text>
                  <br />
                  <Text type="secondary">{alert.message}</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {alert.timestamp}
                  </Text>
                </div>
              </Timeline.Item>
            ))}
          </Timeline>
        </Card>
      </Col>
    </Row>
  );

  // Analysis Tools Tab
  const renderAnalysisTools = () => (
    <Row gutter={[16, 16]}>
      <Col xs={24} md={8}>
        <Card 
          hoverable
          onClick={() => setThreatAnalysisModal(true)}
          style={{ textAlign: 'center', height: 200 }}
        >
          <RadarChartOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
          <Title level={4}>Tehdit Analizi</Title>
          <Text type="secondary">
            Belirli bir lokasyondaki güvenlik tehditlerini analiz edin
          </Text>
        </Card>
      </Col>

      <Col xs={24} md={8}>
        <Card 
          hoverable
          onClick={() => setPhoneAnalysisModal(true)}
          style={{ textAlign: 'center', height: 200 }}
        >
          <PhoneOutlined style={{ fontSize: 48, color: '#52c41a', marginBottom: 16 }} />
          <Title level={4}>Telefon Analizi</Title>
          <Text type="secondary">
            Telefon numaralarını spam/taciz açısından analiz edin
          </Text>
        </Card>
      </Col>

      <Col xs={24} md={8}>
        <Card 
          hoverable
          onClick={() => setRouteAnalysisModal(true)}
          style={{ textAlign: 'center', height: 200 }}
        >
          <EnvironmentOutlined style={{ fontSize: 48, color: '#fa8c16', marginBottom: 16 }} />
          <Title level={4}>Güvenli Rota</Title>
          <Text type="secondary">
            İki nokta arasında en güvenli rotaları bulun
          </Text>
        </Card>
      </Col>

      <Col xs={24} md={12}>
        <Card title="Pattern Dedektörleri" style={{ height: 300 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text>Coğrafi Kümeleme</Text>
              <Switch defaultChecked />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text>Temporal Pattern</Text>
              <Switch defaultChecked />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text>Cross-Source Korelasyon</Text>
              <Switch defaultChecked />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text>Anomali Tespiti</Text>
              <Switch />
            </div>
          </Space>
        </Card>
      </Col>

      <Col xs={24} md={12}>
        <Card title="Sistem Metrikleri" style={{ height: 300 }}>
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <div>
              <Text type="secondary">API Response Time</Text>
              <Progress percent={85} strokeColor="#52c41a" />
            </div>
            <div>
              <Text type="secondary">Database Performance</Text>
              <Progress percent={92} strokeColor="#1890ff" />
            </div>
            <div>
              <Text type="secondary">Collector Success Rate</Text>
              <Progress percent={96} strokeColor="#fa8c16" />
            </div>
            <div>
              <Text type="secondary">Processing Queue</Text>
              <Progress percent={23} strokeColor="#ff4d4f" />
            </div>
          </Space>
        </Card>
      </Col>
    </Row>
  );

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <Space>
            <SecurityScanOutlined style={{ color: '#1890ff' }} />
            🚨 Safety Intelligence Dashboard
          </Space>
        </Title>
        <Text type="secondary">
          GetMyHive güvenlik intelligence operasyon merkezi
        </Text>
      </div>

      {renderStatistics()}

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane 
          tab={
            <Space>
              <DashboardOutlined />
              Genel Bakış
            </Space>
          } 
          key="overview"
        >
          {renderOverview()}
        </TabPane>

        <TabPane 
          tab={
            <Space>
              <SearchOutlined />
              Analiz Araçları
            </Space>
          } 
          key="analysis"
        >
          {renderAnalysisTools()}
        </TabPane>

        <TabPane 
          tab={
            <Space>
              <LineChartOutlined />
              Raporlar
            </Space>
          } 
          key="reports"
        >
          <SafetyIntelligenceReports />
        </TabPane>

        <TabPane 
          tab={
            <Space>
              <SettingOutlined />
              Ayarlar
            </Space>
          } 
          key="settings"
        >
          <SafetyIntelligenceSettings />
        </TabPane>
      </Tabs>

      {/* Threat Analysis Modal */}
      <Modal
        title="🔍 Tehdit Analizi"
        open={threatAnalysisModal}
        onCancel={() => setThreatAnalysisModal(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleThreatAnalysis}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Enlem (Latitude)"
                name="latitude"
                rules={[{ required: true, message: 'Enlem gerekli' }]}
              >
                <InputNumber
                  placeholder="41.0082"
                  style={{ width: '100%' }}
                  step={0.0001}
                  precision={4}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Boylam (Longitude)"
                name="longitude"
                rules={[{ required: true, message: 'Boylam gerekli' }]}
              >
                <InputNumber
                  placeholder="28.9784"
                  style={{ width: '100%' }}
                  step={0.0001}
                  precision={4}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Arama Yarıçapı (metre)"
            name="radius"
            initialValue={1000}
          >
            <InputNumber
              placeholder="1000"
              style={{ width: '100%' }}
              min={50}
              max={5000}
            />
          </Form.Item>

          <Form.Item
            label="Minimum Tehdit Seviyesi"
            name="severity"
            initialValue="medium"
          >
            <Select>
              <Option value="low">Düşük</Option>
              <Option value="medium">Orta</Option>
              <Option value="high">Yüksek</Option>
              <Option value="critical">Kritik</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Zaman Aralığı (saat)"
            name="timeWindow"
            initialValue={24}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              max={168}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
                icon={<SearchOutlined />}
              >
                Analiz Et
              </Button>
              <Button onClick={() => setThreatAnalysisModal(false)}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Phone Analysis Modal */}
      <Modal
        title="📞 Telefon Analizi"
        open={phoneAnalysisModal}
        onCancel={() => setPhoneAnalysisModal(false)}
        footer={null}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handlePhoneAnalysis}
        >
          <Form.Item
            label="Telefon Numarası"
            name="phoneNumber"
            rules={[
              { required: true, message: 'Telefon numarası gerekli' },
              { pattern: /^(\+90|0)?[0-9]{10}$/, message: 'Geçersiz telefon formatı' }
            ]}
          >
            <Input
              placeholder="+905321234567"
              prefix={<PhoneOutlined />}
            />
          </Form.Item>

          <Form.Item
            label="Analiz Seçenekleri"
            name="options"
          >
            <Space direction="vertical">
              <Form.Item name="includePatterns" valuePropName="checked" noStyle>
                <Switch /> Pattern analizi dahil et
              </Form.Item>
              <Form.Item name="includeReports" valuePropName="checked" noStyle>
                <Switch defaultChecked /> Şikayet geçmişi dahil et
              </Form.Item>
            </Space>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
                icon={<SearchOutlined />}
              >
                Analiz Et
              </Button>
              <Button onClick={() => setPhoneAnalysisModal(false)}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Route Analysis Modal */}
      <Modal
        title="🛣️ Güvenli Rota Analizi"
        open={routeAnalysisModal}
        onCancel={() => setRouteAnalysisModal(false)}
        footer={null}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => {
            console.log('Route Analysis:', values);
            setRouteAnalysisModal(false);
          }}
        >
          <Title level={5}>Başlangıç Noktası</Title>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Enlem"
                name={['start', 'latitude']}
                rules={[{ required: true, message: 'Gerekli' }]}
              >
                <InputNumber
                  placeholder="41.0082"
                  style={{ width: '100%' }}
                  step={0.0001}
                  precision={4}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Boylam"
                name={['start', 'longitude']}
                rules={[{ required: true, message: 'Gerekli' }]}
              >
                <InputNumber
                  placeholder="28.9784"
                  style={{ width: '100%' }}
                  step={0.0001}
                  precision={4}
                />
              </Form.Item>
            </Col>
          </Row>

          <Title level={5}>Hedef Nokta</Title>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Enlem"
                name={['end', 'latitude']}
                rules={[{ required: true, message: 'Gerekli' }]}
              >
                <InputNumber
                  placeholder="41.0369"
                  style={{ width: '100%' }}
                  step={0.0001}
                  precision={4}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Boylam"
                name={['end', 'longitude']}
                rules={[{ required: true, message: 'Gerekli' }]}
              >
                <InputNumber
                  placeholder="28.9857"
                  style={{ width: '100%' }}
                  step={0.0001}
                  precision={4}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Zaman Tercihi"
                name="timePreference"
                initialValue="current"
              >
                <Select>
                  <Option value="current">Şu an</Option>
                  <Option value="day">Gündüz</Option>
                  <Option value="evening">Akşam</Option>
                  <Option value="night">Gece</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Max Sapma (km)"
                name="maxDetour"
                initialValue={2}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0.1}
                  max={10}
                  step={0.1}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit" 
                icon={<EnvironmentOutlined />}
              >
                Rota Hesapla
              </Button>
              <Button onClick={() => setRouteAnalysisModal(false)}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Threat Detail Modal */}
      <ThreatDetailModal
        visible={threatDetailVisible}
        threatData={selectedThreat}
        onClose={() => {
          setThreatDetailVisible(false);
          setSelectedThreat(null);
        }}
      />
    </div>
  );
};

export default SafetyIntelligenceDashboard;
